import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';

import {
    TradeBenefitsTierProgressionTile,
    type Bullet,
    type Tier,
} from './TradeBenefitsTierProgressionTile.tsx';

import dibsCss from 'dibs-css';
import styles from './TradeBenefitsTierProgressionTiles.scss';

type TierData = {
    tier: Tier;
    title: ReactNode;
    description: ReactNode;
    bullets: Bullet[];
};

const getTiers = (currency: string): TierData[] => [
    {
        tier: 'silver',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.silver.title"
                defaultMessage="Silver"
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.silver.description"
                defaultMessage="({min}–{max} Points)"
                values={{
                    min: <FormattedNumber value={25000} />,
                    max: <FormattedNumber value={99999} />,
                }}
            />
        ),
        bullets: [
            {
                id: 'all-benefits-above',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.silver.allBenefitsAbove"
                        defaultMessage="All benefits above included"
                    />
                ),
            },
            {
                id: 'specialized-sourcing',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.silver.specializedSourcing"
                        defaultMessage="Specialized Sourcing Support"
                    />
                ),
            },
            {
                id: 'dedicated-account-rep',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.silver.dedicatedAccountRep"
                        defaultMessage="Dedicated Account Representative"
                    />
                ),
            },
            {
                id: 'money-back-60',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.silver.moneyBack60"
                        defaultMessage="60-Day Money-Back Guarantee"
                    />
                ),
            },
            {
                id: 'publicity-consideration',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.silver.publicityConsideration"
                        defaultMessage="Consideration for 1stDibs Publicity Opportunities"
                    />
                ),
            },
        ],
    },
    {
        tier: 'gold',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.gold.title"
                defaultMessage="Gold"
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.gold.description"
                defaultMessage="({min}–{max} Points)"
                values={{
                    min: <FormattedNumber value={100000} />,
                    max: <FormattedNumber value={249999} />,
                }}
            />
        ),
        bullets: [
            {
                id: 'silver-status-included',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.gold.silverStatusIncluded"
                        defaultMessage="Silver status benefits included"
                    />
                ),
            },
            {
                id: 'dedicated-trade-support',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.gold.dedicatedTradeSupport"
                        defaultMessage="Dedicated Trade Support Specialist"
                    />
                ),
            },
            {
                id: 'cash-back-25',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.gold.cashBack25"
                        defaultMessage="{amount} Cash Back for Every {points} Points Earned"
                        values={{
                            amount: (
                                <FormattedNumber
                                    value={25}
                                    style="currency"
                                    currency={currency}
                                    maximumFractionDigits={0}
                                />
                            ),
                            points: <FormattedNumber value={5000} />,
                        }}
                    />
                ),
            },
            {
                id: 'money-back-90',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.gold.moneyBack90"
                        defaultMessage="90-Day Money-Back Guarantee"
                    />
                ),
            },
            {
                id: 'editorial-access',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.gold.editorialAccess"
                        defaultMessage="Access to 1stDibs Editorial Opportunities"
                    />
                ),
            },
        ],
    },
    {
        tier: 'platinum',
        title: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.platinum.title"
                defaultMessage="Platinum"
            />
        ),
        description: (
            <FormattedMessage
                id="abt.TradeBenefitsTierProgressionTiles.platinum.description"
                defaultMessage="({min}+ Points)"
                values={{
                    min: <FormattedNumber value={250000} />,
                }}
            />
        ),
        bullets: [
            {
                id: 'gold-status-included',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.platinum.goldStatusIncluded"
                        defaultMessage="Gold status benefits included"
                    />
                ),
            },
            {
                id: 'cash-back-50',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.platinum.cashBack50"
                        defaultMessage="{amount} Cash Back for Every {points} Points Earned"
                        values={{
                            amount: (
                                <FormattedNumber
                                    value={50}
                                    style="currency"
                                    currency={currency}
                                    maximumFractionDigits={0}
                                />
                            ),
                            points: <FormattedNumber value={5000} />,
                        }}
                    />
                ),
            },
            {
                id: 'money-back-120',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.platinum.moneyBack120"
                        defaultMessage="120-Day Money-Back Guarantee"
                    />
                ),
            },
            {
                id: 'dibs50-consideration',
                content: (
                    <FormattedMessage
                        id="abt.TradeBenefitsTierProgressionTiles.platinum.dibs50Consideration"
                        defaultMessage="Preferential Consideration for Inclusion in the 1stDibs50"
                    />
                ),
            },
        ],
    },
];

const TradeBenefitsTierProgressionTiles: FC = () => {
    const tiers = getTiers('USD');

    return (
        <div className={classnames(styles.grid, dibsCss.grid, dibsCss.gapMedium, dibsCss.pyLarge)}>
            {tiers.map(({ tier, title, description, bullets }) => (
                <TradeBenefitsTierProgressionTile
                    key={tier}
                    tier={tier}
                    title={title}
                    description={description}
                    bullets={bullets}
                />
            ))}
        </div>
    );
};

export { TradeBenefitsTierProgressionTiles };
